/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';

import type { DdpFieldsBelowFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import CareerInsightCard from 'bundles/degree-description/components/modules/career-section/CareerInsightCard';
import CareerIntroCard from 'bundles/degree-description/components/modules/career-section/CareerIntroCard';
import CareerRolesCard from 'bundles/degree-description/components/modules/career-section/CareerRolesCard';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';

type CareerSectionType = DdpFieldsBelowFoldFragment['careerSection'];

type Props = {
  careerSection?: CareerSectionType;
  trackingSection?: SectionName;
};

const styles = {
  sectionContainer: css`
    padding-top: var(--cds-spacing-600);
    padding-bottom: var(--cds-spacing-600);
  `,
};

const CareerModule = ({ careerSection, trackingSection }: Props) => {
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('career_info');

  if (!careerSection) return null;

  /**
   * The order in the Block Entries section from the Contentful API sometimes
   * comes back out of order, but the IDs listed in the main `json`
   * always come back in the right order. This makes sure we grab
   * the correct blocks, no matter how they come back in the Blocks section.
   */
  const orderedCareerBlocksInJson = careerSection.json?.content?.filter((contentItem: Record<string, unknown>) => {
    return contentItem?.nodeType === 'embedded-entry-block';
  });

  if (!orderedCareerBlocksInJson?.[0] || !orderedCareerBlocksInJson?.[1]) return null;

  const careerSectionBlocks = careerSection.links?.entries?.block;

  const insightBlockId = orderedCareerBlocksInJson[0]?.data?.target?.sys?.id;
  const rolesBlockId = orderedCareerBlocksInJson[1]?.data?.target?.sys?.id;

  const insightBlock = careerSectionBlocks.find((contentBlock) => {
    return contentBlock?.sys?.id === insightBlockId;
  });
  const rolesBlock = careerSectionBlocks.find((contentBlock) => {
    return contentBlock?.sys?.id === rolesBlockId;
  });

  return (
    <div ref={pageSectionRef}>
      <Container>
        <Grid container spacing={32} justifyContent="space-between" css={styles.sectionContainer}>
          <Grid item xs={12} md>
            <CareerIntroCard career={careerSection} />
          </Grid>
          {insightBlock && insightBlock.__typename === 'MarketingContent' && (
            <Grid item xs={12} md>
              <CareerInsightCard
                title={insightBlock.title}
                description={insightBlock.description}
                trackingSection={trackingSection}
              />
            </Grid>
          )}
          {rolesBlock && rolesBlock.__typename === 'MarketingContent' && (
            <Grid item xs={12} md>
              <CareerRolesCard
                title={rolesBlock.title}
                description={rolesBlock.description}
                trackingSection={trackingSection}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(CareerModule);
